import React from "react";
import { ThreeDots } from "react-loader-spinner";
import LoadingIcon from "../../../../components/Loading";
import TruckIconFO from "../../../../icons/TruckIconFO";
import RailIconFO from "../../../../icons/RailIconFO";
import { icons } from "../../../../svg";
import { Quote } from "../../../../types/IMultiLocationQuoteMobile";
import type { MissingRailFreightSectionItem } from "../../../../types/IMissingRailFreight";
import type IRouteValidationResult from "../../../competitiveAnalysisModal/components/RouteValidation/types/IRouteValidation";
import type { PublicTariffStatus } from "../../../../types/IRailRateRequest";
import MultiLocSetupRow from "./MultiLocSetupRow";
import RequestTariffSection from "../../manageRailRatePanel/RequestTariffSection";
import {
  formatProductName,
  formatOriginNameDisplay,
  getFavoriteOriginType,
  NoGuidanceSectionItem,
} from "../../multiLocQuoteDetailsPanel/multiLocQuoteDetailsUtils";
import "./multiLocQuoteMissingOrigin.css";

type MissingFreightTab = "TRUCK" | "RAIL";

type MissingMilesOrigin = {
  originId: string;
  originName: string;
  competitor: boolean;
  validation: IRouteValidationResult;
};

type MultiLocQuoteMissingOriginProps = {
  loading: boolean;
  hasQuotesWithoutFreight: boolean;
  shouldShowTruckMissing: boolean;
  shouldShowRailMissing: boolean;
  missingFreightTab: string;
  setMissingFreightTab: (tab: MissingFreightTab) => void;
  originsMissingProductForActiveTab: Quote[];
  showMissingProductHeader: boolean;
  selectedProduct?: string;
  fallbackProduct?: string;
  loadingAddProductByKey: Record<string, boolean>;
  onReviewResolveClick: (quote: Quote) => void;
  onAddMissingProduct: (quote: Quote) => void;
  missingMilesOrigins: MissingMilesOrigin[];
  loadingMileageRequestByOrigin: Record<string, boolean>;
  onMileageRequest: (origin: MissingMilesOrigin) => void | Promise<void>;
  loadingMissingMiles: boolean;
  originsWithNoGuidance: NoGuidanceSectionItem[];
  manageRailRate: boolean;
  railMissingQuotesForDisplay: Quote[];
  originsMissingRailFreightForDisplay: MissingRailFreightSectionItem[];
  requestedRailRateByOriginKey: Record<string, boolean>;
  failedRailRateRequestByOriginKey: Record<string, boolean>;
  loadingRailRateRequestByOriginKey: Record<string, boolean>;
  publicTariffStatusByOriginKey: Record<string, PublicTariffStatus>;
  publicTariffRateByOriginKey: Record<string, string>;
  onRequestMultipleRailRates: () => void;
  onRequestSingleRailOrigin: (origin: MissingRailFreightSectionItem) => void;
  onCheckPublicTariffForOrigin: (origin: MissingRailFreightSectionItem) => void;
  loadingRailMissingFreight: boolean;
};

const MultiLocQuoteMissingOrigin: React.FC<MultiLocQuoteMissingOriginProps> = ({
  loading,
  hasQuotesWithoutFreight,
  shouldShowTruckMissing,
  shouldShowRailMissing,
  missingFreightTab,
  setMissingFreightTab,
  originsMissingProductForActiveTab,
  showMissingProductHeader,
  selectedProduct,
  fallbackProduct,
  loadingAddProductByKey,
  onReviewResolveClick,
  onAddMissingProduct,
  missingMilesOrigins,
  loadingMileageRequestByOrigin,
  onMileageRequest,
  loadingMissingMiles,
  originsWithNoGuidance,
  manageRailRate,
  railMissingQuotesForDisplay,
  originsMissingRailFreightForDisplay,
  requestedRailRateByOriginKey,
  failedRailRateRequestByOriginKey,
  loadingRailRateRequestByOriginKey,
  publicTariffStatusByOriginKey,
  publicTariffRateByOriginKey,
  onRequestMultipleRailRates,
  onRequestSingleRailOrigin,
  onCheckPublicTariffForOrigin,
  loadingRailMissingFreight,
}) => {
  if (
    loading ||
    !hasQuotesWithoutFreight ||
    (!shouldShowTruckMissing && !shouldShowRailMissing)
  ) {
    return null;
  }

  return (
    <div
      id="missing-origins"
      className="missing-origins-container multi-loc-quote-missing-origin"
    >
      <div className="inbound-modes-title">Missing Origins</div>
      <div id="missing-freight-section-id" className="missing-freight-section">
        <div id="missing-freight-content-id">
          <div id="fml-toggle-track" className="fml-toggle-track">
            {shouldShowTruckMissing && shouldShowRailMissing ? (
              <>
                <button
                  id="truck-missing-toggle-button"
                  type="button"
                  className={`fml-toggle-pill ${
                    missingFreightTab === "TRUCK" ? "active" : ""
                  }`}
                  onClick={() => setMissingFreightTab("TRUCK")}
                >
                  <TruckIconFO
                    color={
                      missingFreightTab === "TRUCK" ? "#1D4ED8" : "#6B7280"
                    }
                  />
                  <span
                    className={`fml-toggle-label ${
                      missingFreightTab === "TRUCK"
                        ? "fml-toggle-label-truck-active"
                        : "fml-toggle-label-inactive"
                    }`}
                  >
                    Truck
                  </span>
                </button>

                <button
                  type="button"
                  id="rail-missing-toggle-button"
                  className={`fml-toggle-pill ${
                    missingFreightTab === "RAIL" ? "active" : ""
                  }`}
                  onClick={() => setMissingFreightTab("RAIL")}
                >
                  <RailIconFO
                    color={missingFreightTab === "RAIL" ? "#F97316" : "#6B7280"}
                  />
                  <span
                    className={`fml-toggle-label ${
                      missingFreightTab === "RAIL"
                        ? "fml-toggle-label-rail-active"
                        : "fml-toggle-label-inactive"
                    }`}
                  >
                    Rail
                  </span>
                </button>
              </>
            ) : (
              <div className="fml-toggle-pill active fml-toggle-pill-single">
                {shouldShowTruckMissing ? (
                  <>
                    <TruckIconFO color="#1D4ED8" />
                    <span className="fml-toggle-label fml-toggle-label-truck-active">
                      Truck
                    </span>
                  </>
                ) : (
                  <>
                    <RailIconFO color="#F97316" />
                    <span className="fml-toggle-label fml-toggle-label-rail-active">
                      Rail
                    </span>
                  </>
                )}
              </div>
            )}
          </div>

          {originsMissingProductForActiveTab.length > 0 && (
            <div id="origins-missing-product">
              {showMissingProductHeader && (
                <div
                  id="origins-missing-product-title"
                  className="origins-needing-setup-title"
                >
                  Origins Missing Product
                </div>
              )}
              {originsMissingProductForActiveTab.map((quote) => {
                const missingProductName = formatProductName(
                  selectedProduct || fallbackProduct || "AMMONIA",
                );
                const favoriteOriginType = getFavoriteOriginType(quote);
                const addProductKey = `${
                  quote.origin.id || ""
                }:${favoriteOriginType}:${missingProductName.toUpperCase()}`;
                const quoteKey = `${quote.competitor ? "competitor" : "koch"}-${
                  quote.origin.id || quote.origin.name
                }-TRUCK`;
                const showProductLoadingStyle =
                  !!loadingAddProductByKey[addProductKey];
                const addMissingProductIcon = showProductLoadingStyle ? (
                  <ThreeDots color="#64748B" height={14} width={20} />
                ) : (
                  <img
                    id="add-missing-product-icon"
                    className="truck-setup-action-plus"
                    src={icons.AddIcon}
                    alt="Add Product"
                  />
                );
                const addMissingProductText = showProductLoadingStyle
                  ? "Adding..."
                  : `Add ${missingProductName.toUpperCase()}`;

                return (
                  <div
                    id="truck-add-product-item"
                    className="truck-setup-item truck-setup-item-inline-layout"
                    key={`setup-${quoteKey}`}
                  >
                    <div
                      id={`need-setup-${quoteKey}`}
                      className={`truck-setup-origin-name ${
                        quote.competitor
                          ? "truck-setup-origin-name-competitor"
                          : "truck-setup-origin-name-koch"
                      }`}
                    >
                      {formatOriginNameDisplay(quote.origin.name)}
                    </div>

                    <button
                      id="add-missing-product-button"
                      type="button"
                      className="truck-setup-action-btn"
                      onClick={() => onAddMissingProduct(quote)}
                      disabled={showProductLoadingStyle}
                    >
                      {addMissingProductIcon}
                      <span
                        id="add-missing-product-text"
                        className="truck-setup-action-text"
                      >
                        {addMissingProductText}
                      </span>
                    </button>
                  </div>
                );
              })}
            </div>
          )}
          <div id="need-guidance-content" className="setup-list-wrapper">
            {shouldShowTruckMissing &&
              missingFreightTab === "TRUCK" &&
              !loadingMissingMiles &&
              missingMilesOrigins.length > 0 && (
                <div
                  id="missing-miles-section"
                  className="missing-miles-section"
                >
                  <div className="origins-needing-setup-title">
                    Origins Missing Miles
                  </div>
                  {missingMilesOrigins.map((missingMilesOrigin) => {
                    const mileageOriginLoadingKey = `${
                      missingMilesOrigin.competitor ? "competitor" : "koch"
                    }-${missingMilesOrigin.originId}`;
                    const requestIsLoading =
                      !!loadingMileageRequestByOrigin[mileageOriginLoadingKey];
                    const requestSent =
                      !!missingMilesOrigin.validation?.mileageRequested;

                    let requestIcon = (
                      <span className="truck-setup-action-plus">
                        <img
                          id="add-missing-product-icon"
                          className="truck-setup-action-plus"
                          src={icons.AddIcon}
                          alt="Add Product"
                        />
                      </span>
                    );
                    let requestText = "Request";

                    if (requestIsLoading) {
                      requestIcon = (
                        <ThreeDots color="#64748B" height={14} width={20} />
                      );
                      requestText = "Requesting...";
                    } else if (requestSent) {
                      requestIcon = (
                        <img
                          className="truck-setup-success-icon"
                          src={icons.GreenCheckMark}
                          alt="Mileage Requested"
                        />
                      );
                      requestText = "Requested";
                    }

                    return (
                      <div
                        className="truck-setup-item missing-miles-row"
                        key={`missing-miles-${missingMilesOrigin.originId}`}
                      >
                        <div
                          id={`missing-miles-origin-name-${missingMilesOrigin.originId}`}
                          className={`truck-setup-origin-name ${
                            missingMilesOrigin.competitor
                              ? "truck-setup-origin-name-competitor"
                              : "truck-setup-origin-name-koch"
                          }`}
                        >
                          {formatOriginNameDisplay(
                            missingMilesOrigin.originName,
                          )}
                        </div>

                        <div
                          id={`missing-miles-actions-${missingMilesOrigin.originId}`}
                          className="truck-setup-actions"
                        >
                          <button
                            id="request-missing-miles-button"
                            type="button"
                            className={`truck-setup-action-btn ${
                              requestSent
                                ? "truck-setup-action-btn-success"
                                : ""
                            }`}
                            onClick={() => onMileageRequest(missingMilesOrigin)}
                            disabled={requestIsLoading || requestSent}
                          >
                            {requestIcon}
                            <span
                              className={`truck-setup-action-text ${
                                requestSent
                                  ? "truck-setup-action-text-success"
                                  : ""
                              }`}
                            >
                              {requestText}
                            </span>
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}

            {shouldShowTruckMissing &&
              missingFreightTab === "TRUCK" &&
              originsWithNoGuidance.length > 0 && (
                <div
                  id="origins-with-no-guidance-section"
                  className="outside-milage-band-section"
                >
                  <div className="origins-needing-setup-title">
                    Origins With No Guidance
                  </div>

                  {originsWithNoGuidance.map((outsideOrigin) => (
                    <div
                      className="truck-setup-item outside-mileage-row"
                      key={`outside-mileage-${outsideOrigin.originKey}`}
                    >
                      <div
                        id={`outside-mileage-origin-name-${
                          outsideOrigin.originId || outsideOrigin.originKey
                        }`}
                        className={`truck-setup-origin-name ${
                          outsideOrigin.competitor
                            ? "truck-setup-origin-name-competitor"
                            : "truck-setup-origin-name-koch"
                        }`}
                      >
                        {formatOriginNameDisplay(outsideOrigin.originName)}
                      </div>

                      <div
                        id={`no-guidance-description-${
                          outsideOrigin.originId || outsideOrigin.originKey
                        }`}
                        className={`outside-mileage-chip ${
                          (outsideOrigin.description || "").startsWith(
                            "Outside Mileage Band -",
                          )
                            ? "outside-mileage-chip-wrap"
                            : ""
                        }`}
                      >
                        {outsideOrigin.description}
                      </div>
                    </div>
                  ))}
                </div>
              )}

            {shouldShowRailMissing &&
              !manageRailRate &&
              missingFreightTab === "RAIL" &&
              railMissingQuotesForDisplay.map((quote) => (
                <MultiLocSetupRow
                  key={`setup-${quote.competitor ? "competitor" : "koch"}-${
                    quote.origin.id || quote.origin.name
                  }-rail`}
                  quote={quote}
                  onReviewResolveClick={onReviewResolveClick}
                  textColor={quote.competitor ? "#991B1B" : "#1D4ED8"}
                />
              ))}

            {shouldShowRailMissing &&
              missingFreightTab === "RAIL" &&
              manageRailRate &&
              originsMissingRailFreightForDisplay.length > 0 && (
                <RequestTariffSection
                  originsMissingRailFreight={
                    originsMissingRailFreightForDisplay
                  }
                  requestedRailRateByOriginKey={requestedRailRateByOriginKey}
                  failedRailRateRequestByOriginKey={
                    failedRailRateRequestByOriginKey
                  }
                  loadingRailRateRequestByOriginKey={
                    loadingRailRateRequestByOriginKey
                  }
                  publicTariffStatusByOriginKey={publicTariffStatusByOriginKey}
                  publicTariffRateByOriginKey={publicTariffRateByOriginKey}
                  onRequestMultiple={onRequestMultipleRailRates}
                  onRequestSingleOrigin={onRequestSingleRailOrigin}
                  onCheckPublicTariff={onCheckPublicTariffForOrigin}
                />
              )}
          </div>
        </div>
      </div>

      {shouldShowTruckMissing &&
        missingFreightTab === "TRUCK" &&
        loadingMissingMiles && (
          <div className="missing-freight-loading-overlay">
            <LoadingIcon height={48} width={48} color="#60A5FA" />
          </div>
        )}

      {shouldShowRailMissing &&
        missingFreightTab === "RAIL" &&
        manageRailRate &&
        loadingRailMissingFreight && (
          <div className="missing-freight-loading-overlay">
            <LoadingIcon height={48} width={48} color="#60A5FA" />
          </div>
        )}
    </div>
  );
};

export default MultiLocQuoteMissingOrigin;
